// auditpm-v7
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'

const ACTION_COLORS = {
  'Quotation created': { bg: '#E0F2F2', color: '#004a4a' },
  'Quotation reviewed': { bg: '#EAF3DE', color: '#27500a' },
  'Quotation approved': { bg: '#EAF3DE', color: '#27500a' },
  'Quotation rejected': { bg: '#FDECEA', color: '#791F1F' },
  'Quotation sent': { bg: '#E0F2F2', color: '#004a4a' },
  'Auditor approved': { bg: '#EAF3DE', color: '#27500a' },
  'Auditor rejected': { bg: '#FDECEA', color: '#791F1F' },
  'User role updated': { bg: '#FFF3E0', color: '#854F0B' },
  'Audit plan sent': { bg: '#E0F2F2', color: '#004a4a' },
  'COI High risk': { bg: '#FDECEA', color: '#791F1F' },
  'COI Medium risk': { bg: '#FFF3E0', color: '#854F0B' },
  'Assignment override approved': { bg: '#EAF3DE', color: '#27500a' },
}

function getColor(action) {
  for (const [key, val] of Object.entries(ACTION_COLORS)) {
    if (action?.includes(key)) return val
  }
  return { bg: 'var(--bg2)', color: 'var(--text2)' }
}

export default function AuditTrail() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ entity: '', performer: '', from: '', to: '' })

  useEffect(() => { load() }, [])

  async function load() {
    try {
      const { data, error } = await supabase.from('audit_trail')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500)
      if (error) { console.error('audit_trail query error:', error); setLogs([]); }
      else setLogs(data || [])
    } catch(e) { console.error('AuditTrail load error:', e); setLogs([]) }
    setLoading(false)
  }

  const filtered = logs.filter(l => {
    if (filter.entity && !((l.entity_label || '') + (l.entity_type || '')).toLowerCase().includes(filter.entity.toLowerCase())) return false
    if (filter.performer && !(l.performer_name || '').toLowerCase().includes(filter.performer.toLowerCase())) return false
    if (filter.from && l.created_at < filter.from) return false
    if (filter.to && l.created_at > filter.to + 'T23:59:59') return false
    return true
  })

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Audit trail</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>
            Non-editable log of all actions. {logs.length} records.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Search entity…" value={filter.entity} onChange={e => setFilter(f => ({ ...f, entity: e.target.value }))} style={{ maxWidth: 200 }} />
        <input placeholder="Search user…" value={filter.performer} onChange={e => setFilter(f => ({ ...f, performer: e.target.value }))} style={{ maxWidth: 180 }} />
        <input type="date" value={filter.from} onChange={e => setFilter(f => ({ ...f, from: e.target.value }))} style={{ width: 'auto' }} />
        <input type="date" value={filter.to} onChange={e => setFilter(f => ({ ...f, to: e.target.value }))} style={{ width: 'auto' }} />
        {(filter.entity || filter.performer || filter.from || filter.to) && (
          <button className="sm" onClick={() => setFilter({ entity: '', performer: '', from: '', to: '' })}>Clear filters</button>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date / time</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Performed by</th>
                <th>Role</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>No records found</td></tr>}
              {filtered.map(l => {
                const col = getColor(l.action)
                return (
                  <tr key={l.id}>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap', color: 'var(--text2)' }}>
                      {l.created_at ? format(parseISO(l.created_at), 'd MMM yyyy HH:mm') : '—'}
                    </td>
                    <td>
                      <span style={{ fontSize: 13, padding: '2px 8px', borderRadius: 12, background: col.bg, color: col.color, fontWeight: 500 }}>
                        {l.action}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <div style={{ fontWeight: 500 }}>{l.entity_label || '—'}</div>
                      <div style={{ color: 'var(--text3)', fontSize: 13, textTransform: 'capitalize' }}>{l.entity_type}</div>
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 500 }}>{l.performer_name}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{l.performer_role}</td>
                    <td style={{ fontSize: 13, color: 'var(--text3)', maxWidth: 200 }}>
                      {l.details && Object.keys(l.details).length > 0 && (
                        <details>
                          <summary style={{ cursor: 'pointer', color: 'var(--accent)' }}>View</summary>
                          <div style={{ marginTop: 4 }}>
                            {Object.entries(l.details).map(([k, v]) => (
                              <div key={k}><strong>{k}:</strong> {String(v)}</div>
                            ))}
                          </div>
                        </details>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>
        Audit trail records are permanent and cannot be edited or deleted.
      </div>
    </div>
  )
}
